/**
 * facelet 索引(0..53) → (cubie 逻辑坐标, 所在面) 映射 —— 自 cubeMesh.js 平移。
 * 这套映射经过全部 17 个教学案例验证，禁止重写。
 *
 * 坐标 → 面（右手系，y 朝上）：+x=R, -x=L, +y=U, -y=D, +z=F, -z=B
 */
import * as THREE from 'three';
import type { Face } from '../core/cube';

export interface FaceletInfo {
  pos: [number, number, number];
  face: Face;
}

function buildFaceletMap(): FaceletInfo[] {
  const map = new Array<FaceletInfo>(54);
  const setFace = (face: Face, cells: [number, number, number][]) => {
    const base = { U: 0, R: 9, F: 18, D: 27, L: 36, B: 45 }[face];
    cells.forEach((c, i) => {
      map[base + i] = { pos: c, face };
    });
  };

  // U 面：从 +y 向下看，行 = -z（B→F），列 = +x（L→R）
  setFace('U', [
    [-1, 1, -1], [0, 1, -1], [1, 1, -1],
    [-1, 1, 0], [0, 1, 0], [1, 1, 0],
    [-1, 1, 1], [0, 1, 1], [1, 1, 1],
  ]);
  // D 面：从 -y 向上看，行 = +z（F→B），列 = +x
  setFace('D', [
    [-1, -1, 1], [0, -1, 1], [1, -1, 1],
    [-1, -1, 0], [0, -1, 0], [1, -1, 0],
    [-1, -1, -1], [0, -1, -1], [1, -1, -1],
  ]);
  // F 面：行 = -y（上→下），列 = +x
  setFace('F', [
    [-1, 1, 1], [0, 1, 1], [1, 1, 1],
    [-1, 0, 1], [0, 0, 1], [1, 0, 1],
    [-1, -1, 1], [0, -1, 1], [1, -1, 1],
  ]);
  // B 面：行 = -y，列 = -x（R→L）
  setFace('B', [
    [1, 1, -1], [0, 1, -1], [-1, 1, -1],
    [1, 0, -1], [0, 0, -1], [-1, 0, -1],
    [1, -1, -1], [0, -1, -1], [-1, -1, -1],
  ]);
  // R 面：行 = -y，列 = -z（F→B）
  setFace('R', [
    [1, 1, 1], [1, 1, 0], [1, 1, -1],
    [1, 0, 1], [1, 0, 0], [1, 0, -1],
    [1, -1, 1], [1, -1, 0], [1, -1, -1],
  ]);
  // L 面：行 = -y，列 = +z（B→F）
  setFace('L', [
    [-1, 1, -1], [-1, 1, 0], [-1, 1, 1],
    [-1, 0, -1], [-1, 0, 0], [-1, 0, 1],
    [-1, -1, -1], [-1, -1, 0], [-1, -1, 1],
  ]);

  return map;
}

export const FACELET_MAP = buildFaceletMap();

/** 面字母 → 外法线 */
export const FACE_NORMAL: Record<Face, THREE.Vector3> = {
  U: new THREE.Vector3(0, 1, 0),
  D: new THREE.Vector3(0, -1, 0),
  R: new THREE.Vector3(1, 0, 0),
  L: new THREE.Vector3(-1, 0, 0),
  F: new THREE.Vector3(0, 0, 1),
  B: new THREE.Vector3(0, 0, -1),
};
